import { useState, useEffect, useCallback } from 'react';
import { ticketsAPI } from '@/lib/api';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Loader2, History, AlertTriangle, PowerOff, FileClock, Sparkles } from 'lucide-react';

/**
 * Casos parecidos al que estoy mirando.
 *
 * **Lo pidió Facundo así:** *"me llega el ticket, yo lo tomo, y en algún apartado me dice: hubo 5
 * tickets iguales, la resolución fue ésta"*. La búsqueda ya existía y se usaba **sólo** para
 * inyectarla en el prompt del agente — ninguna pantalla la llamaba.
 *
 * **La mitad de este componente es el vacío, y es a propósito.** Medido el 2026-08-25: 1 evento
 * aprobado, 4 borradores, 0 casos cerrados. Por un buen rato el estado normal de este panel **es**
 * el vacío. Y hay tres vacíos que piden cosas distintas —la memoria apagada, nada aprobado
 * todavía, y nada que se parezca— más un cuarto que no es vacío sino un fallo. Un panel en blanco
 * los vuelve idénticos, y quien lo abre una vez y ve la nada no lo abre nunca más.
 */
const VACIOS = {
  memoria_apagada: { icono: PowerOff, tono: 'text-zinc-500', titulo: 'La memoria está apagada' },
  sin_aprobados: { icono: FileClock, tono: 'text-amber-700', titulo: 'Todavía no hay nada aprobado' },
  ninguno_se_parece: { icono: Sparkles, tono: 'text-zinc-500', titulo: 'Este caso es nuevo' },
  // **No es un vacío: es un fallo.** Mostrarlo como los otros diría que el caso es nuevo cuando
  // en realidad no se pudo averiguar, y quien lo lee arrancaría de cero creyendo que nadie lo
  // resolvió antes.
  no_se_pudo_buscar: { icono: AlertTriangle, tono: 'text-red-700', titulo: 'No se pudo consultar la memoria' },
};

export default function CasosParecidos({ tenantId, capsuleId, ticketId, onIrAlCaso }) {
  const [datos, setDatos] = useState(null);
  const [cargando, setCargando] = useState(true);

  const cargar = useCallback(async () => {
    if (!tenantId || !capsuleId || !ticketId) return;
    setCargando(true);
    try {
      const { data } = await ticketsAPI.similares(tenantId, capsuleId, ticketId);
      setDatos(data);
    } catch (_) {
      setDatos({ casos: [], por_que_vacio: 'no_se_pudo_buscar',
                 detalle: 'No se pudo consultar la memoria. No quiere decir que no haya nada parecido.' });
    } finally {
      setCargando(false);
    }
  }, [tenantId, capsuleId, ticketId]);

  useEffect(() => { cargar(); }, [cargar]);

  if (cargando) {
    return (
      <p className="text-xs text-zinc-500 flex items-center gap-2 py-6 justify-center">
        <Loader2 className="w-3.5 h-3.5 animate-spin" /> Buscando casos parecidos…
      </p>
    );
  }
  if (!datos) return null;

  const casos = datos.casos || [];

  if (!casos.length) {
    const v = VACIOS[datos.por_que_vacio] || VACIOS.ninguno_se_parece;
    const Icono = v.icono;
    return (
      <Card className="rounded-sm border-brand-border" data-testid="casos-parecidos-vacio">
        <CardContent className="p-5 flex items-start gap-3">
          <Icono className={`w-4 h-4 mt-0.5 shrink-0 ${v.tono}`} />
          <div className="space-y-1">
            <p className={`text-xs font-semibold ${v.tono}`}>{v.titulo}</p>
            <p className="text-[11px] text-zinc-500 max-w-xl">{datos.detalle}</p>
            {datos.borradores > 0 && (
              <p className="text-[11px] text-amber-800">
                Hay <b>{datos.borradores}</b> {datos.borradores === 1 ? 'borrador' : 'borradores'} esperando
                revisión. Cada uno aprobado es un caso que el próximo no tiene que resolver de nuevo.
              </p>
            )}
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-2" data-testid="casos-parecidos">
      <p className="text-[10px] text-zinc-500 flex items-center gap-1.5">
        <History className="w-3 h-3" />
        {casos.length} {casos.length === 1 ? 'caso parecido' : 'casos parecidos'} ya resueltos
        {datos.optica ? <span className="text-zinc-400">· óptica {datos.optica}</span> : null}
      </p>

      {casos.map((c) => (
        <Card key={c.event_id} className="rounded-sm border-brand-border">
          <CardContent className="p-3 space-y-2">
            <div className="flex items-start justify-between gap-2">
              <p className="text-xs font-semibold text-[#09090B]">{c.titulo}</p>
              {c.caso_original && (
                <button
                  type="button"
                  data-testid={`ir-al-caso-${c.caso_original}`}
                  onClick={() => onIrAlCaso?.(c.caso_original)}
                  className="text-[10px] text-brand-primary hover:underline shrink-0"
                  title="Abrir el caso original: el resumen no siempre alcanza"
                >
                  {c.caso_original}
                </button>
              )}
            </div>

            {c.sintomas && (
              <p className="text-[11px] text-zinc-600"><b>Pasaba:</b> {c.sintomas}</p>
            )}
            {c.causa && (
              <p className="text-[11px] text-zinc-600"><b>Era:</b> {c.causa}</p>
            )}
            {c.pasos?.length > 0 && (
              <div className="text-[11px] text-zinc-700">
                <b>Se hizo:</b>
                <ul className="list-disc pl-4 mt-0.5 space-y-0.5">
                  {c.pasos.map((p, i) => <li key={i}>{p}</li>)}
                </ul>
              </div>
            )}

            {/* La confianza a la vista: una resolución que escribió una persona y una que un
                modelo dedujo de un chat no valen lo mismo, y quien la lee tiene que poder
                distinguirlas antes de aplicarla. */}
            <Badge variant="outline" className="text-[9px] rounded-sm">
              confianza {Math.round((c.confianza || 0) * 100)}%
            </Badge>
          </CardContent>
        </Card>
      ))}
    </div>
  );
}
